// B1 Hoy: los números que mandan (semana y hoy) y lo que hay que hacer hoy.
import { Bell, Check } from "lucide-react";
import { useState } from "react";
import { useCreate, useGetList, useNotify, useRefresh, useUpdate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { dinero, fechaCorta, hoyISO, sumarDias } from "@/reclutamiento/logica";
import type { MetricaDiaria } from "@/reclutamiento/tipos";
import { esDemo } from "@/reclutamiento/modo";
import { Encabezado, Kpi, Pildora, Seccion } from "../comun";
import {
  useAnuncios,
  useAvisos,
  useCandidatos,
  useClientes,
  useMetricas,
  usePagos,
  useSolicitudes,
  useUsuariosPortal,
  useVacantes,
} from "../datos";

export const Hoy = () => {
  const hoy = hoyISO();
  const ini = sumarDias(hoy, -6);
  const { data: deals = [] } = useGetList("deals", { pagination: { page: 1, perPage: 5000 }, sort: { field: "id", order: "ASC" } });
  const { data: clientes } = useClientes();
  const { data: vacantes } = useVacantes();
  const { data: candidatos } = useCandidatos();
  const { data: pagos } = usePagos();
  const { data: metricas } = useMetricas();
  const { data: avisos } = useAvisos();
  const { data: solicitudes } = useSolicitudes();
  const { data: usuarios } = useUsuariosPortal();
  const { data: anuncios } = useAnuncios();

  const semana = metricas.filter((m) => m.fecha >= ini && m.fecha <= hoy);
  const mensajes = semana.reduce((a, m) => a + Number(m.mensajes), 0);
  const mensajesHoy = metricas.find((m) => m.fecha === hoy)?.mensajes ?? 0;
  const gasto = semana.reduce((a, m) => a + Number(m.gasto_pauta), 0);
  const calificados = deals.filter((d: any) => d.calificado_en && d.calificado_en >= ini);
  const calificadosHoy = calificados.filter((d: any) => d.calificado_en === hoy).length;
  const deClientes = candidatos.filter((c) => c.cliente_id);
  const entregadosSem = deClientes.filter((c) => c.recibido_en.slice(0, 10) >= ini);
  const entregadosHoy = deClientes.filter((c) => c.recibido_en.slice(0, 10) === hoy).length;
  const entrevistados = deClientes.filter((c) => c.etapa === "entrevistado" || c.etapa === "contratado").length;
  const contratados = deClientes.filter((c) => c.etapa === "contratado").length;
  const pendientes = pagos.filter((p) => p.estado === "pendiente");
  const vencidos = pendientes.filter((p) => p.vence < hoy);
  const activos = vacantes.filter((v) => v.estado === "activa");
  const sinSeguimiento = deals.filter(
    (d: any) => ["nuevo", "calificando", "en-cierre"].includes(d.stage) && (!d.siguiente_fecha || d.siguiente_fecha <= hoy),
  );
  const vacSinCandidatos = activos.filter((v) => !candidatos.some((c) => c.vacante_id === v.id));
  const borradores = vacantes.filter((v) => v.estado === "borrador");
  const invitaciones = usuarios.filter((u) => u.estado === "pendiente_invitacion");
  const cambios = solicitudes.filter((s) => !s.atendida_en);
  const nombreCliente = (id: string | null | undefined) => clientes.find((c) => c.id === id)?.nombre ?? "—";
  const pendienteTotal = sinSeguimiento.length + vencidos.length + vacSinCandidatos.length + borradores.length + invitaciones.length + cambios.length;

  return (
    <div>
      <Encabezado
        ceja={`Semana del ${fechaCorta(ini)} al ${fechaCorta(hoy)}`}
        titulo="Hoy"
        sub="Los números que mandan. El gasto de pauta se captura a mano abajo (V1)."
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi titulo="Mensajes" valor={mensajes} detalle={`${mensajesHoy} hoy`} />
        <Kpi titulo="Prospectos calificados" valor={calificados.length} detalle={`${calificadosHoy} hoy`} />
        <Kpi titulo="Costo por calificado" valor={calificados.length ? dinero(gasto / calificados.length) : "—"} detalle={`Pauta de la semana: ${dinero(gasto)}`} />
        <Kpi titulo="Clientes activos" valor={clientes.length} detalle={`${activos.length} vacantes activas${borradores.length ? ` · ${borradores.length} en borrador` : ""}`} />
        <Kpi titulo="Candidatos entregados" valor={entregadosSem.length} detalle={`${entregadosHoy} hoy · ${deClientes.length} en total`} />
        <Kpi titulo="Entrevistados" valor={entrevistados} detalle="Lo reportan los clientes en su portal" />
        <Kpi titulo="Contratados" valor={contratados} tono={contratados ? "ok" : undefined} detalle="Lo reportan los clientes en su portal" />
        <Kpi titulo="Pagos pendientes" valor={pendientes.length} tono={vencidos.length ? "bad" : undefined} detalle={`${dinero(pendientes.reduce((a, p) => a + Number(p.monto), 0))}${vencidos.length ? ` · ${vencidos.length} vencido(s)` : ""}`} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Seccion titulo={<>Qué hacer hoy <span className="num text-muted-foreground">({pendienteTotal})</span></>}>
          <ul className="divide-y text-sm">
            {sinSeguimiento.map((d: any) => (
              <Fila key={`d${d.id}`} tono="revisar" etiqueta="Prospecto sin seguimiento">
                <Link className="font-medium hover:underline" to={`/deals/${d.id}/show`}>{d.perfil?.contacto ?? d.name}</Link>{" "}
                <span className="text-muted-foreground">· {d.siguiente_paso || "sin siguiente paso"}{d.siguiente_fecha ? ` (${fechaCorta(d.siguiente_fecha)})` : ""}</span>
              </Fila>
            ))}
            {vencidos.map((p) => (
              <Fila key={p.id} tono="bad" etiqueta="Pago vencido">
                <Link className="font-medium hover:underline" to="/pagos">{nombreCliente(p.cliente_id)}</Link> <span className="text-muted-foreground">· {p.periodo} · {dinero(p.monto)} · venció {fechaCorta(p.vence)}</span>
              </Fila>
            ))}
            {vacSinCandidatos.map((v) => (
              <Fila key={v.id} tono="revisar" etiqueta="Vacante sin candidatos">
                <Link className="font-medium hover:underline" to={`/vacantes/${v.id}/show`}>{v.puesto}</Link> <span className="text-muted-foreground">· {nombreCliente(v.cliente_id)}</span>
              </Fila>
            ))}
            {borradores.map((v) => (
              <Fila key={`b${v.id}`} tono="acento" etiqueta="Vacante por confirmar y firmar">
                <Link className="font-medium hover:underline" to={`/vacantes/${v.id}/show`}>{v.puesto}</Link> <span className="text-muted-foreground">· {nombreCliente(v.cliente_id)}</span>
              </Fila>
            ))}
            {invitaciones.map((u) => (
              <Fila key={u.id} tono="acento" etiqueta="Mandar invitación al portal">
                <Link className="font-medium hover:underline" to={`/clientes/${u.cliente_id}/show`}>{u.nombre}</Link> <span className="text-muted-foreground">· {u.correo || "falta el correo"} · {nombreCliente(u.cliente_id)}</span>
              </Fila>
            ))}
            {cambios.map((s) => <FilaCambio key={s.id} s={s} cliente={nombreCliente(s.cliente_id)} puesto={vacantes.find((v) => v.id === s.vacante_id)?.puesto ?? ""} />)}
            {pendienteTotal === 0 ? <li className="py-3 text-muted-foreground">Nada pendiente. 🎉</li> : null}
          </ul>
        </Seccion>
        <div className="space-y-4">
          <CapturaPauta metricas={metricas} />
          <Seccion titulo={<span className="flex items-center gap-2"><Bell className="size-4" /> Avisos</span>}>
            <ul className="space-y-2 text-sm">
              {avisos.slice(0, 8).map((a) => (
                <li key={a.id} className="rounded-lg border p-2">
                  <span className="block text-xs text-muted-foreground">{fechaCorta(a.creado_en)} · {a.tipo === "rocio" ? "a Rocío" : a.tipo}</span>
                  {a.deal_id ? <Link className="hover:underline" to={`/deals/${a.deal_id}/show`}>{a.texto}</Link> : a.texto}
                </li>
              ))}
              {!avisos.length ? <li className="text-muted-foreground">Sin avisos.</li> : null}
            </ul>
          </Seccion>
        </div>
      </div>

      <Seccion titulo="Anuncios para empresas (prospectos)" className="mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground"><tr><th className="py-1 pr-3">Anuncio</th><th className="pr-3">Mensajes</th><th className="pr-3">Prospectos</th><th className="pr-3">Calificados</th><th className="pr-3">Clientes</th><th className="pr-3">Gasto</th><th>Costo por calificado</th></tr></thead>
            <tbody>
              {anuncios.filter((a) => a.tipo === "empresas").map((a) => {
                const ps = deals.filter((d: any) => d.anuncio === a.codigo);
                const cal = ps.filter((d: any) => d.calificado_en).length;
                return (
                  <tr key={a.id} className="border-t">
                    <td className="py-2 pr-3"><Pildora tono="acento">{a.codigo}</Pildora> <span className="text-xs text-muted-foreground">{a.tema}</span></td>
                    <td className="num pr-3">{a.mensajes}</td>
                    <td className="num pr-3">{ps.length}</td>
                    <td className="num pr-3">{cal}</td>
                    <td className="num pr-3">{ps.filter((d: any) => d.cliente_id).length}</td>
                    <td className="num pr-3">{dinero(a.gasto)}</td>
                    <td className="num">{cal ? dinero(a.gasto / cal) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Seccion>
      {esDemo ? (
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Modo demo: los datos viven en este navegador.{" "}
          <button
            className="underline"
            onClick={async () => {
              if (!window.confirm("¿Regresar los datos de prueba a como venían? Se pierden los cambios de esta demo.")) return;
              const { reiniciarDemo } = await import("@/reclutamiento/demo/almacen");
              reiniciarDemo();
              window.location.reload();
            }}
          >
            Reiniciar datos de prueba
          </button>
        </p>
      ) : null}
    </div>
  );
};

const Fila = ({ tono, etiqueta, children }: { tono: "ok" | "revisar" | "bad" | "acento"; etiqueta: string; children: React.ReactNode }) => (
  <li className="flex flex-wrap items-center gap-2 py-2">
    <Pildora tono={tono}>{etiqueta}</Pildora>
    <span className="min-w-0">{children}</span>
  </li>
);

const FilaCambio = ({ s, cliente, puesto }: { s: { id: string; texto: string }; cliente: string; puesto: string }) => {
  const [update] = useUpdate();
  const refresh = useRefresh();
  return (
    <li className="flex flex-wrap items-center gap-2 py-2">
      <Pildora tono="acento">Pide un cambio</Pildora>
      <span className="min-w-0 flex-1"><b>{cliente}</b> · {puesto}: «{s.texto}»</span>
      <Button size="sm" variant="ghost" onClick={() => update("solicitudes_cambio", { id: s.id, data: { atendida_en: new Date().toISOString() }, previousData: s }, { onSuccess: refresh })}>
        <Check className="size-4" /> Atendida
      </Button>
    </li>
  );
};

const CapturaPauta = ({ metricas }: { metricas: MetricaDiaria[] }) => {
  const hoy = hoyISO();
  const actual = metricas.find((m) => m.fecha === hoy);
  const [monto, setMonto] = useState("");
  const [mensajes, setMensajes] = useState("");
  const [update] = useUpdate();
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const guardar = () => {
    const data = {
      fecha: hoy,
      gasto_pauta: monto === "" ? (actual?.gasto_pauta ?? 0) : Number(monto),
      mensajes: mensajes === "" ? (actual?.mensajes ?? 0) : Number(mensajes),
    };
    const ok = { onSuccess: () => { setMonto(""); setMensajes(""); notify("Guardado", { type: "info" }); refresh(); } };
    if (actual) update("metricas_diarias", { id: actual.id, data, previousData: actual }, ok);
    else create("metricas_diarias", { data: { id: hoy, ...data } }, ok);
  };
  return (
    <Seccion titulo="Captura de hoy (a mano)">
      <p className="mb-2 text-xs text-muted-foreground">Hoy van {dinero(actual?.gasto_pauta ?? 0)} de pauta y {actual?.mensajes ?? 0} mensajes. En V2 esto se lee directo de Meta.</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Gasto de pauta $<input className="mt-1 block h-9 w-28 rounded-md border bg-background px-2 text-sm" inputMode="numeric" value={monto} onChange={(e) => setMonto(e.target.value)} placeholder={String(actual?.gasto_pauta ?? 0)} /></label>
        <label className="text-xs">Mensajes<input className="mt-1 block h-9 w-24 rounded-md border bg-background px-2 text-sm" inputMode="numeric" value={mensajes} onChange={(e) => setMensajes(e.target.value)} placeholder={String(actual?.mensajes ?? 0)} /></label>
        <Button size="sm" onClick={guardar} disabled={monto === "" && mensajes === ""}>Guardar</Button>
      </div>
    </Seccion>
  );
};

export default Hoy;
